import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState, useLocale,
} from '@hk/ui'
import {
  fetchNotifications, formatCount, formatDateTime, markNotificationsSeen, nameIn,
  translateError, type Notification,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-77 — WHAT HAPPENED TO YOUR WORK WHILE YOU WERE DOING IT.  Sprint 75.

   The client's answer on notifications was **"in-app only for phase 1"**, and that settles
   more than the delivery channel: it means this screen is the ONLY place a person will ever
   be told these things, which makes what is on it the whole question.

   ── IT IS NOT W-61, AND IF IT WERE IT SHOULD NOT EXIST ─────────────────────────
   "Needs you" answers *what is outstanding now that nothing else can move* — a supervisor's
   shift, in four cards. This answers a different question for a different person: **what
   was done to my work, by somebody else, while I was not there.**

   ── EVERY ROW NAMES THE PERSON WHO DID IT ──────────────────────────────────────
   "Your room was taken off you" is an event. *"Haider took 412 off you — the guest came
   back for their bag"* is something a man can act on, or at least stop wondering about. A
   notification that reports an act without naming the actor is the shape that makes people
   believe the system does things to them.

   ── AND THERE IS NO SETTINGS SCREEN, DELIBERATELY ──────────────────────────────
   W-118 in the packs is where somebody chooses their channels. **In-app only for phase 1
   means there is nothing to choose**, and a screen of toggles for email and SMS that
   deliver nothing is #90's shape exactly — a promise nobody keeps, discovered by a
   supervisor who turned one on and waited. It is not built, and this is where that is
   written down.
   ========================================================================= */

export function Notifications() {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: ({ signal }) => fetchNotifications({ signal }),
    retry: false,
  })

  const seen = useMutation({
    mutationFn: markNotificationsSeen,
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  if (notifications.isLoading && !notifications.error) return <LoadingState rows={5} />

  if (notifications.error && !notifications.data) {
    const translated = translateError(notifications.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={notifications.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = notifications.data!

  return (
    <div className="space-y-4 p-4">
      {view.items.length === 0 ? (
        /* **"NOTHING HAS HAPPENED" IS NOT "YOU ARE UP TO DATE".** A man who has never
            looked and a man with nothing waiting are in the same place, and the sentence
            that fits both is about the hotel rather than about him. */
        <EmptyState title={t.ntfNothingTitle} body={t.ntfNothingBody} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm">
              {view.unread > 0
                ? fill(t.ntfUnread, { count: formatCount(view.unread, locale) })
                : t.ntfAllSeen}
            </p>
            {view.unread > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ms-auto"
                onClick={() => seen.mutate()}
                disabled={seen.isPending}
              >
                <CheckCheck className="size-3.5" />
                {t.ntfMarkSeen}
              </Button>
            )}
          </div>

          <Card>
            <CardHeader title={t.ntfTitle} icon={<Bell className="size-4" />} />
            <CardBody className="space-y-2.5">
              {/* **KEYED BY POSITION, BECAUSE A NOTIFICATION HAS NO IDENTITY.** There is
                  no notification table — each row is derived from a fact elsewhere — so
                  there is no id to key on, and code-plus-time-plus-subject is not unique:
                  a room given and taken back in the same second by the same hand collides.
                  The list holds no input and nothing animates between renders, so position
                  is safe here in a way it usually is not. */}
              {view.items.map((item, at) => (
                <Row key={at} item={item} />
              ))}
            </CardBody>
          </Card>

          {/* **WHAT THIS SCREEN DOES NOT DO, SAID ON IT.** A person who has read four
              lines here will reasonably assume it holds everything; saying what it covers
              is what stops them waiting on it for something it was never going to say. */}
          <p className="text-[12px] text-muted-foreground">{t.ntfScope}</p>
        </>
      )}
    </div>
  )
}

function Row({ item }: { item: Notification }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  /* **THE SENTENCE IS BUILT HERE, FROM THE FIELDS, IN THE READER'S LANGUAGE.** The code is
     never translated because it is what a client branches on; everything beside it is
     data. The same rule as W-61's cards. */
  const who = item.who ? nameIn(locale, item.who, item.whoAr) : t.ntfSomebody

  return (
    <div className="flex items-start gap-2 border-b pb-2.5 last:border-0 last:pb-0">
      {/* **UNREAD IS A MARK, NOT A COLOUR ON THE WHOLE ROW.** A screen where half the rows
          are tinted is one whose tint means nothing by the second week. */}
      <span
        aria-hidden
        className={`mt-1.5 size-1.5 shrink-0 rounded-full ${item.isUnread ? '' : 'opacity-0'}`}
        style={{ backgroundColor: 'var(--hk-primary)' }}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm">
          {fill(t.ntfSentence[item.code] ?? item.code, {
            who,
            room: item.roomNumber ?? t.ntfARoom,
          })}
        </span>

        {/* Verbatim, and the guest's or the supervisor's own words are never translated. */}
        {item.reason && (
          <span className="block text-[12px] text-muted-foreground" dir="auto">
            {item.reason}
          </span>
        )}

        <span className="block text-[11.5px] text-muted-foreground">
          {formatDateTime(new Date(item.atUtc), locale)}
        </span>
      </span>
    </div>
  )
}
